import React from 'react';
import { Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { GxpSensorRow } from './types';
import { useBreakpoints } from '@/hooks/useBreakpoints';

interface SensorsTableProps {
  sensors: GxpSensorRow[];
}

const SensorsTable: React.FC<SensorsTableProps> = ({ sensors }) => {
  const { xs, sm } = useBreakpoints();

  const formatTemperature = (value?: number | null) =>
    value != null ? `${value} °C` : '-';

  const numericColumns: ColumnsType<GxpSensorRow> = [
    {
      title: 'Min',
      dataIndex: 'min',
      key: 'min',
      render: formatTemperature,
    },
    {
      title: 'Max',
      dataIndex: 'max',
      key: 'max',
      render: (val: number | null | undefined, row) => (
        <span
          className={
            (row.c_min_high ?? 0) > 0 ? 'gxp-table-cell--danger' : undefined
          }
        >
          {formatTemperature(val)}
        </span>
      ),
    },
    {
      title: 'Avg',
      dataIndex: 'avg',
      key: 'avg',
      render: formatTemperature,
    },
    {
      title: 'Med',
      dataIndex: 'med',
      key: 'med',
      render: formatTemperature,
    },
    {
      title: 'StdDev',
      dataIndex: 'std_dev',
      key: 'std_dev',
      render: formatTemperature,
    },
    {
      title: 'MKT',
      dataIndex: 'mkt',
      key: 'mkt',
      render: formatTemperature,
    },
    {
      title: '°C-min H / L',
      key: 'cMin',
      render: (_: unknown, row) => (
        <span
          className={
            (row.c_min_high ?? 0) > 0 || (row.c_min_low ?? 0) > 0
              ? 'gxp-table-cell--danger'
              : undefined
          }
        >
          {row.c_min_high} / {row.c_min_low}
        </span>
      ),
    },
    { title: 'In-window', dataIndex: 'in_window', key: 'in_window' },
    {
      title: 'Completeness',
      key: 'completeness',
      render: (_: unknown, row) => (
        <span
          className={row.completeness_warning ? 'gxp-table-cell' : undefined}
        >
          {row.completeness_count} / {row.completeness_total}
        </span>
      ),
    },
  ];

  const columns: ColumnsType<GxpSensorRow> = [
    {
      title: 'Sensor',
      key: 'sensor',
      fixed: xs || sm ? undefined : 'left',
      render: (_: unknown, row) => (
        <div className='gxp-sensor-cell'>
          <div className='gxp-sensor-cell__product'>{row.product_label}</div>
          {row.provisional && (
            <div className='gxp-table-cell--warning'>
              Provisional — awaiting data
            </div>
          )}
          <div className='gxp-sensor-cell__device'>{row.device_label}</div>
          {row.calibration_link_label && (
            <div className='gxp-sensor-cell__calibration-row'>
              <a className='gxp-sensor-cell__calibration' href='#calibration'>
                {row?.calibration_link_label}
              </a>
              {row.calibration_label && <span> - {row.calibration_label}</span>}
            </div>
          )}
          <div className='gxp-sensor-cell__window'>
            Start: {row?.window_start || '--'}
          </div>
          <div className='gxp-sensor-cell__window'>
            End: {row.window_end || '--'}
          </div>
        </div>
      ),
    },
    ...numericColumns,
  ];

  return (
    <div className='gxp-card'>
      <div className='gxp-card__title'>Sensors</div>
      <div className='settings-content-wrapper'>
        <Table
          className='gxp-table settings-table'
          columns={columns}
          dataSource={sensors}
          rowKey='id'
          pagination={false}
          scroll={{ x: true }}
        />
      </div>
    </div>
  );
};

export default SensorsTable;
